/**
 * Fetchers client-side du blog réflexif pour le lecteur in-app (migration
 * 0220). Client Supabase authentifié standard (`@/lib/supabase`) — la RLS
 * `blog_articles: read_public_published` autorise déjà `authenticated` sur
 * `visibility='public' AND status='published'`, pas besoin de service-role.
 *
 * Le vote passe par les RPC additives `record_blog_poll_vote_app` /
 * `get_blog_my_poll_votes_app` (auth.uid(), pas de vote_token newsletter) —
 * distinctes des RPC marketing (`record_blog_poll_vote`, vote_token).
 */

import { supabase } from '@/lib/supabase';
import {
  BLOG_VOTE_EVENT,
  groupBlogPollCounts,
  type BlogArticle,
  type BlogArticleSummary,
  type BlogPollCount,
} from '@jeraisonne/db';

export interface BlogAuthor {
  display_name: string;
  avatar_url: string | null;
}

export interface BlogArticleWithAuthor extends BlogArticle {
  author: BlogAuthor | null;
}

const FEED_SUMMARY_COLS =
  'id, slug, title, subtitle, illustration_url, reading_time, view_count, target_roles, created_at, published_at';

/**
 * Articles publics+publiés, du plus récent. Pas de filtre par rôle côté
 * requête (comme les autres requêtes de `use-news-feed.ts`) — le filtrage
 * `target_roles` se fait côté hook via `isBlogArticleVisibleToRole`.
 */
export async function fetchBlogFeedArticles(limit = 20): Promise<BlogArticleSummary[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select(FEED_SUMMARY_COLS)
    .eq('visibility', 'public')
    .eq('status', 'published')
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as BlogArticleSummary[];
}

export async function fetchBlogArticleBySlug(slug: string): Promise<BlogArticleWithAuthor | null> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select(
      '*, author:profiles!blog_articles_author_id_fkey(display_name:visible_name, avatar_url)',
    )
    .eq('slug', slug)
    .eq('visibility', 'public')
    .eq('status', 'published')
    .maybeSingle();
  if (error || !data) return null;
  const row = data as unknown as BlogArticle & { author: BlogAuthor | null };
  const author = row.author?.display_name ? row.author : null;
  return { ...row, author };
}

export async function recordBlogPollVoteApp(
  articleId: string,
  blockIndex: number,
  choiceIndex: number,
): Promise<{ counts: BlogPollCount[] }> {
  const { data, error } = await supabase.rpc('record_blog_poll_vote_app', {
    p_article_id: articleId,
    p_block_index: blockIndex,
    p_choice_index: choiceIndex,
  });
  if (error) throw error;
  return data as unknown as { counts: BlogPollCount[] };
}

export interface MyBlogPollVotes {
  votes: { block_index: number; choice_index: number }[];
  counts: Record<number, BlogPollCount[]>;
}

/** Réconciliation avec la BDD au montage : réponses de l'utilisateur + distribution agrégée. */
export async function fetchMyBlogPollVotesApp(articleId: string): Promise<MyBlogPollVotes> {
  const { data, error } = await supabase.rpc('get_blog_my_poll_votes_app', {
    p_article_id: articleId,
  });
  if (error || !data) return { votes: [], counts: {} };
  const result = data as unknown as {
    votes?: { block_index: number; choice_index: number }[];
    counts?: BlogPollCount[];
  };
  const votes = Array.isArray(result.votes) ? result.votes : [];
  const counts = groupBlogPollCounts(result.counts);
  return { votes, counts };
}

export async function incrementBlogView(articleId: string): Promise<void> {
  await supabase.rpc('increment_blog_view', { p_article_id: articleId });
}

export async function recordBlogBlockEvent(
  articleId: string,
  blockIndex: number,
  event: 'level1_view' | 'level2_open' | 'level3_open',
): Promise<void> {
  await supabase.rpc('record_blog_block_event', {
    p_article_id: articleId,
    p_block_index: blockIndex,
    p_event: event,
  });
}

/** Best-effort — null en cas d'erreur (l'appelant garde la valeur courante). */
export async function fetchBlogActiveReadersCount(articleId: string): Promise<number | null> {
  const { data, error } = await supabase.rpc('get_blog_active_readers', {
    p_article_id: articleId,
  });
  if (error || data == null) return null;
  return typeof data === 'number' ? data : Number(data);
}

/**
 * Réveille les écouteurs du badge « raisonneurs » (même onglet, pas d'event
 * `storage`). Défini ici (pas dans `@jeraisonne/db`, qui n'a pas la lib DOM
 * dans son tsconfig) — seul `dispatchEvent` touche `window`.
 */
export function emitBlogVoteRecorded(): void {
  try {
    window.dispatchEvent(new Event(BLOG_VOTE_EVENT));
  } catch {
    /* best-effort */
  }
}
